import {
  useCallback,
  useMemo,
  useReducer,
  type PropsWithChildren,
} from "react";
import {
  NavigationContext,
  type ForwardHistory,
  type NavigateToPayload,
  type NavigationContextValue,
  type NavigationLevel,
  type NavigationState,
} from "./navigationContextCore";

type NavigationAction =
  | { type: "GO_TO_DASHBOARD" }
  | { type: "GO_TO_GOAL"; goalId: string }
  | { type: "GO_TO_TASK"; goalId: string; taskId: string }
  | { type: "GO_TO_SUBTASK"; goalId: string; taskId: string; subTaskId: string }
  | {
      type: "JUMP_TO_LEVEL";
      level: NavigationLevel;
      ids?: { goalId?: string | null; taskId?: string | null; subTaskId?: string | null };
    }
  | { type: "LEVEL_DOWN"; payload?: NavigateToPayload }
  | { type: "GO_FORWARD" }
  | { type: "GO_BACK" }
  | { type: "SET_EDITING"; isEditing: boolean }
  | { type: "TOGGLE_EDITING" }
  | { type: "RESET" };

const initialNavigationState: NavigationState = {
  currentLevel: "dashboard",
  activeGoalId: null,
  activeTaskId: null,
  activeSubTaskId: null,
  forwardHistoryMode: false,
  isEditing: false,
  forwardHistory: null,
};

const levelRank: Record<NavigationLevel, number> = {
  dashboard: 0,
  goal: 1,
  task: 2,
  subtask: 3,
};

const createForwardHistory = (state: NavigationState): ForwardHistory | null => {
  if (state.currentLevel === "dashboard" || !state.activeGoalId) {
    return null;
  }

  // Capture the exact node before moving upward so a breadcrumb can jump back down.
  return {
    level: state.currentLevel,
    activeGoalId: state.activeGoalId,
    activeTaskId: state.activeTaskId,
    activeSubTaskId: state.activeSubTaskId,
  };
};

const clearForwardHistory = (
  state: NavigationState,
  preserveForwardHistory = false,
): Pick<NavigationState, "forwardHistory" | "forwardHistoryMode"> => {
  if (preserveForwardHistory) {
    return {
      forwardHistory: state.forwardHistory,
      forwardHistoryMode: state.forwardHistoryMode,
    };
  }

  return {
    forwardHistory: null,
    forwardHistoryMode: false,
  };
};

const canUseForwardHistory = (state: NavigationState): boolean =>
  state.forwardHistoryMode &&
  Boolean(state.forwardHistory) &&
  levelRank[state.forwardHistory?.level ?? "dashboard"] > levelRank[state.currentLevel];

const consumeForwardHistory = (state: NavigationState): NavigationState => {
  if (!state.forwardHistory || !canUseForwardHistory(state)) {
    return state;
  }

  return {
    ...state,
    currentLevel: state.forwardHistory.level,
    activeGoalId: state.forwardHistory.activeGoalId,
    activeTaskId: state.forwardHistory.activeTaskId,
    activeSubTaskId: state.forwardHistory.activeSubTaskId,
    isEditing: false,
    forwardHistory: null,
    forwardHistoryMode: false,
  };
};

const navigationReducer = (
  state: NavigationState,
  action: NavigationAction,
): NavigationState => {
  switch (action.type) {
    case "GO_TO_DASHBOARD":
      return {
        ...state,
        currentLevel: "dashboard",
        activeGoalId: null,
        activeTaskId: null,
        activeSubTaskId: null,
        isEditing: false,
        forwardHistory: null,
        forwardHistoryMode: false,
      };

    case "GO_TO_GOAL":
      return {
        ...state,
        currentLevel: "goal",
        activeGoalId: action.goalId,
        activeTaskId: null,
        activeSubTaskId: null,
        isEditing: false,
        forwardHistory: null,
        forwardHistoryMode: false,
      };

    case "GO_TO_TASK":
      return {
        ...state,
        currentLevel: "task",
        activeGoalId: action.goalId,
        activeTaskId: action.taskId,
        activeSubTaskId: null,
        isEditing: false,
        forwardHistory: null,
        forwardHistoryMode: false,
      };

    case "GO_TO_SUBTASK":
      return {
        ...state,
        currentLevel: "subtask",
        activeGoalId: action.goalId,
        activeTaskId: action.taskId,
        activeSubTaskId: action.subTaskId,
        isEditing: false,
        forwardHistory: null,
        forwardHistoryMode: false,
      };

    case "JUMP_TO_LEVEL": {
      if (action.level === "dashboard") {
        return {
          ...state,
          currentLevel: "dashboard",
          activeGoalId: null,
          activeTaskId: null,
          activeSubTaskId: null,
          isEditing: false,
          forwardHistory: null,
          forwardHistoryMode: false,
        };
      }

      if (action.level === "goal" && action.ids?.goalId) {
        return {
          ...state,
          currentLevel: "goal",
          activeGoalId: action.ids.goalId,
          activeTaskId: null,
          activeSubTaskId: null,
          isEditing: false,
          forwardHistory: null,
          forwardHistoryMode: false,
        };
      }

      if (action.level === "task" && action.ids?.goalId && action.ids?.taskId) {
        return {
          ...state,
          currentLevel: "task",
          activeGoalId: action.ids.goalId,
          activeTaskId: action.ids.taskId,
          activeSubTaskId: null,
          isEditing: false,
          forwardHistory: null,
          forwardHistoryMode: false,
        };
      }

      if (
        action.level === "subtask" &&
        action.ids?.goalId &&
        action.ids?.taskId &&
        action.ids?.subTaskId
      ) {
        return {
          ...state,
          currentLevel: "subtask",
          activeGoalId: action.ids.goalId,
          activeTaskId: action.ids.taskId,
          activeSubTaskId: action.ids.subTaskId,
          isEditing: false,
          forwardHistory: null,
          forwardHistoryMode: false,
        };
      }

      return state;
    }

    case "LEVEL_DOWN": {
      // Calling levelDown with no explicit child follows the smart breadcrumb path.
      if (!action.payload && canUseForwardHistory(state)) {
        return consumeForwardHistory(state);
      }

      const payload = action.payload ?? {};
      const forwardState = clearForwardHistory(state, payload.preserveForwardHistory);

      if (state.currentLevel === "dashboard" && payload.goalId) {
        return {
          ...state,
          ...forwardState,
          currentLevel: "goal",
          activeGoalId: payload.goalId,
          activeTaskId: null,
          activeSubTaskId: null,
          isEditing: false,
        };
      }

      if (state.currentLevel === "goal" && state.activeGoalId && payload.taskId) {
        return {
          ...state,
          ...forwardState,
          currentLevel: "task",
          activeTaskId: payload.taskId,
          activeSubTaskId: null,
          isEditing: false,
        };
      }

      if (
        state.currentLevel === "task" &&
        state.activeGoalId &&
        state.activeTaskId &&
        payload.subTaskId
      ) {
        return {
          ...state,
          ...forwardState,
          currentLevel: "subtask",
          activeSubTaskId: payload.subTaskId,
          isEditing: false,
        };
      }

      return state;
    }

    case "GO_FORWARD": {
      return consumeForwardHistory(state);
    }

    case "GO_BACK": {
      const forwardHistory = createForwardHistory(state);

      if (state.currentLevel === "subtask") {
        return {
          ...state,
          currentLevel: "task",
          activeSubTaskId: null,
          isEditing: false,
          forwardHistory,
          forwardHistoryMode: Boolean(forwardHistory),
        };
      }

      if (state.currentLevel === "task") {
        return {
          ...state,
          currentLevel: "goal",
          activeTaskId: null,
          activeSubTaskId: null,
          isEditing: false,
          forwardHistory,
          forwardHistoryMode: Boolean(forwardHistory),
        };
      }

      if (state.currentLevel === "goal") {
        return {
          ...state,
          currentLevel: "dashboard",
          activeGoalId: null,
          activeTaskId: null,
          activeSubTaskId: null,
          isEditing: false,
          forwardHistory,
          forwardHistoryMode: Boolean(forwardHistory),
        };
      }

      return state;
    }

    case "SET_EDITING":
      return {
        ...state,
        isEditing: action.isEditing,
      };

    case "TOGGLE_EDITING":
      return {
        ...state,
        isEditing: !state.isEditing,
      };

    case "RESET":
      return initialNavigationState;

    default:
      return state;
  }
};

export const NavigationProvider = ({ children }: PropsWithChildren) => {
  const [state, dispatch] = useReducer(navigationReducer, initialNavigationState);

  const goToDashboard = useCallback(() => {
    dispatch({ type: "GO_TO_DASHBOARD" });
  }, []);

  const goToGoal = useCallback((goalId: string) => {
    dispatch({ type: "GO_TO_GOAL", goalId });
  }, []);

  const goToTask = useCallback((goalId: string, taskId: string) => {
    dispatch({ type: "GO_TO_TASK", goalId, taskId });
  }, []);

  const goToSubTask = useCallback((goalId: string, taskId: string, subTaskId: string) => {
    dispatch({ type: "GO_TO_SUBTASK", goalId, taskId, subTaskId });
  }, []);

  const jumpToLevel = useCallback(
    (
      level: NavigationLevel,
      ids?: { goalId?: string | null; taskId?: string | null; subTaskId?: string | null },
    ) => {
      dispatch({ type: "JUMP_TO_LEVEL", level, ids });
    },
    [],
  );

  const levelDown = useCallback((payload?: NavigateToPayload) => {
    dispatch({ type: "LEVEL_DOWN", payload });
  }, []);

  const goForward = useCallback(() => {
    dispatch({ type: "GO_FORWARD" });
  }, []);

  const goBack = useCallback(() => {
    dispatch({ type: "GO_BACK" });
  }, []);

  const setEditing = useCallback((isEditing: boolean) => {
    dispatch({ type: "SET_EDITING", isEditing });
  }, []);

  const toggleEditing = useCallback(() => {
    dispatch({ type: "TOGGLE_EDITING" });
  }, []);

  const resetNavigation = useCallback(() => {
    dispatch({ type: "RESET" });
  }, []);

  const value = useMemo<NavigationContextValue>(
    () => ({
      ...state,
      canGoBack: state.currentLevel !== "dashboard",
      canGoForward: canUseForwardHistory(state),
      goToDashboard,
      goToGoal,
      goToTask,
      goToSubTask,
      jumpToLevel,
      levelDown,
      goForward,
      goBack,
      setEditing,
      toggleEditing,
      resetNavigation,
    }),
    [
      goBack,
      goForward,
      goToDashboard,
      goToGoal,
      goToSubTask,
      goToTask,
      jumpToLevel,
      levelDown,
      resetNavigation,
      setEditing,
      state,
      toggleEditing,
    ],
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};
